import React, { useEffect, useReducer, useRef, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import { RbacPermissions } from '../../../core/interfaces/user';
import { FILTER_ALL } from '../../../list/constants/listFilter';
import {
  HdButton,
  HdIcon,
  HdIconButton,
  HdMenu,
  HdMenuItem,
  HdRbacButton,
  HdTooltip
} from '../../../components';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import styles from './styles.module.scss';
import { SyncHistoryStatusEnum } from '../ActivationSyncHistory/models/sync-history-status';
import {
  FilterList,
  GetList,
  ListNextPage,
  ListResponse,
  RefreshList
} from '../../../list/pagination/list-conductor/list-actions';
import useListConductor from '../../../list/hooks/useListConductor';
import { OffsetPaginationStrategy } from '../../../list/pagination/list-conductor/offset-pagination-strategy';
import { EntityUIState } from '../../../core/interfaces/entitiy-ui-state';
import {
  TRACKER_ACTIVATION_SYNC_HISTORY_HISTORY_FETCH_INIT,
  TRACKER_ACTIVATION_SYNC_HISTORY_RECORDS_IGNORED_BUTTON_CLICK,
  TRACKER_ACTIVATION_SYNC_HISTORY_SYNC_FAILURES_BUTTON_CLICK,
  TRACKER_ACTIVATION_SYNC_HISTORY_SYNC_FAILURES_FETCH_FAILED,
  TRACKER_ACTIVATION_SYNC_HISTORY_SYNC_FAILURES_FETCH_SUCCESS,
  TRACKER_ACTIVATION_SYNC_HISTORY_VIEW_DETAILS_CLICK
} from '../tracking';
import { useActivationHistorySyncHistoryService } from './useActivationSyncHistoryService';
import { numFormatter } from '../../../core/utils/number';
import { SyncHistoryItem } from '../ActivationSyncHistory/models/sync-history-item';
import HdCalendar from '../../../date-time/components/calendar-format/HdCalendar';
import { ActivationRunStageEnum } from '../ActivationSyncHistory/models/run-stages';
import { addSuffixWhenNounPlural } from '../../../core/utils/string';
import HdDuration from '../../../date-time/components/duration/HdDuration';
import { isEven } from '../../../core/utils/mathUtil';
import RecordsIgnored from '../RecordsIgnored';
import RecordsFailure from '../RecordsFailure';
import ActivationsAPI from '../api/ActivationsAPI';
import {
  INITIAL_FAILED_RECORDS_STATE,
  recordsFailureReducer,
  RecordsStatus
} from '../RecordsFailure/recordsFailureReducer';
import { HdCoordinatedHeight } from '../../../layout/components/coordinated-height/HdCoordinatedHeight';
import ActivationSyncHistoryItemDialog from '../ActivationSyncHistoryItemDialog';
import {
  HdCoordinatedScroll,
  HdCoordinatedScrollAnchor
} from '../../../a11y/scroll/components/coordinated-scroll/HdCoordinatedScroll';
import useDontMountAtFirst from '../../../core/hooks/useDontMountAtFirst';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import {
  SYNC_HISTORY_PAGE_SIZE,
  SYNC_HISTORY_SHIMMER_DIMENSIONS,
  SYNC_HISTORY_STATUS_FILTER_KEY
} from '../constants/constants';
import { RecordsFailureContext } from '../RecordsFailure/recordsFailureContext';
import ScrollToTopButton from '../../../a11y/scroll/components/scroll-to-top-button/ScrollToTopButton';
import FilterIcon from '../../../list/components/filter-icon/FilterIcon';
import BoxTableShimmer from '../../../layout/components/box-table-shimmer/BoxTableShimmer';
import RetryApiAlert from '../../../fallback-screens/components/retry-api-alert/RetryApiAlert';

function ActivationSyncHistoryTable({ activationId, activationSeqId }) {
  const location = useLocation();

  const [statusFilterDropdownOpen, setStatusFilterDropdownOpen] = useState(false);
  const [ignoredRunId, setIgnoredRunId] = useState<number>();
  const [failedRunId, setFailedRunId] = useState<number>();
  const [hasLastRunFailedRecords, setHasLastRunFailedRecords] = useState(false);
  const [syncHistoryItems, setSyncHistoryItems] = useState([]);
  const [showOpenDetailsDialog, setShowOpenDetailsDialog] = useState(false);
  const [currentSyncItem, setCurrentSyncItem] = useState<any>();
  const analyticsTracker = useAnalyticsTracker();
  const coordinatedHeightTargetRef = useRef(null);
  const coordinatedScrollRef = useRef(null);
  const coordinatedScrollAnchorRef = useRef(null);

  const { data, getItems } = useActivationHistorySyncHistoryService();

  const [failedRecordsState, dispatch] = useReducer(
    recordsFailureReducer,
    INITIAL_FAILED_RECORDS_STATE
  );

  const { listConductor, changes } = useListConductor({
    paginationStrategy: new OffsetPaginationStrategy(SYNC_HISTORY_PAGE_SIZE)
  });

  const listConductorState = listConductor.getListState().state;
  const listConductorStateError = listConductor.getListState().error;
  const listConductorMeta = listConductor.getListMeta();
  const { totalCount } = listConductorMeta.pagination;

  const statusFilterOptions = [FILTER_ALL, ...SyncHistoryStatusEnum.toArray()];
  const statusFilterValue = listConductorMeta.filters?.[SYNC_HISTORY_STATUS_FILTER_KEY];

  const shimmerDimensions = SYNC_HISTORY_SHIMMER_DIMENSIONS;

  const [lastElementRef] = useInfiniteScroll({
    loading:
      listConductorState === EntityUIState.LOADING ||
      listConductorState === EntityUIState.REFRESHING ||
      listConductorState === EntityUIState.LOADING_MORE,
    hasNextPage: totalCount >= SYNC_HISTORY_PAGE_SIZE,
    onLoadMore: () => {
      listConductor.dispatch(new ListNextPage());
    },
    disabled: listConductorState === EntityUIState.ERRORED,
    rootMargin: '0px 0px 400px 0px'
  });

  const canShowTable =
    listConductorState === EntityUIState.IDLE ||
    listConductorState === EntityUIState.NEW ||
    listConductorState === EntityUIState.EMPTY ||
    listConductorState === EntityUIState.LOADING_MORE;

  useEffect(() => {
    if (data) {
      if (listConductor.getListMetaNetworkParams().page === 0) {
        setSyncHistoryItems(data);
      } else {
        setSyncHistoryItems(prev => [...prev, ...data]);
      }

      listConductor.dispatch(new ListResponse({ count: data.length }));
    }

    const highlightFailed = new URLSearchParams(location.search).get('highlight');

    if (highlightFailed) {
      const lastRunFailedRecords = syncHistoryItems[0]?.getRecordsFailed();

      if (lastRunFailedRecords > 0) {
        setHasLastRunFailedRecords(true);
        setFailedRunId(syncHistoryItems[0].id);
      }
    }
  }, [data]);

  useDontMountAtFirst(() => {
    const { isLastPage } = listConductor._options.paginationStrategy;
    if (!isLastPage) {
      listConductor.dispatch(new ListNextPage());
    }

    getActivationRuns();
  }, [changes]);

  useEffect(() => {
    setSyncHistoryItems([]);
    listConductor.dispatch(new GetList());
  }, [activationId]);

  const onStatusFilterTriggerClick = () => {
    setStatusFilterDropdownOpen(true);
  };

  const onRefresh = () => {
    setSyncHistoryItems([]);
    listConductor.dispatch(new RefreshList(true));
  };

  const getActivationRuns = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_HISTORY_FETCH_INIT,
      properties: {
        activationId
      }
    });

    getItems(activationId, listConductor.getListMetaNetworkParams());
  };

  const onItemRecordsIgnoredClick = (syncItem: SyncHistoryItem) => {
    analyticsTracker.eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_RECORDS_IGNORED_BUTTON_CLICK,
      properties: {
        activationSeqId,
        activationRunId: syncItem.id
      }
    });

    if (syncItem.id === ignoredRunId) {
      setIgnoredRunId(null);
      return;
    }

    if (failedRunId) {
      setFailedRunId(null);
    }

    setIgnoredRunId(syncItem.id);
  };

  const shouldShowIgnoredEventsDetails = (syncItemId: number) => ignoredRunId === syncItemId;

  const shouldShowFailedEventsDetails = (syncItemId: number) => {
    return failedRunId === syncItemId;
  };

  const getIgnoredRunTransformationStats = () => {
    if (!ignoredRunId) {
      return null;
    }

    const ignoredRunDetails = syncHistoryItems.find(
      syncHistoryItem => syncHistoryItem.id === ignoredRunId
    );
    return ignoredRunDetails.transformationRunStats;
  };

  const onItemRecordsFailedClick = (syncItem: SyncHistoryItem) => {
    analyticsTracker.eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_SYNC_FAILURES_BUTTON_CLICK,
      properties: {
        activationSeqId,
        activationRunId: syncItem.id
      }
    });

    if (hasLastRunFailedRecords) {
      setHasLastRunFailedRecords(false);
    }

    if (syncItem.id === failedRunId) {
      setFailedRunId(null);
      return;
    }

    if (ignoredRunId) {
      setIgnoredRunId(null);
    }

    setFailedRunId(syncItem.id);
  };

  const isSyncStatusFailed = (syncItem: SyncHistoryItem) => syncItem.status.isFailedStatus;

  const getItemRecordsSuccessTooltip = (syncItem: SyncHistoryItem) => {
    if (syncItem.status.activationRunStages.includes(ActivationRunStageEnum.TRANSFORMATION)) {
      const transformationSuccess = syncItem.transformationRunStats.recordsSuccess;

      return `${transformationSuccess} ${addSuffixWhenNounPlural('record', transformationSuccess)}
       transformed successfully`;
    }

    const loadingSuccess = syncItem.loadingRunStats.recordsSuccess;

    return `${loadingSuccess} ${addSuffixWhenNounPlural('record', loadingSuccess)}
      synced successfully to Target`;
  };

  const showDetailsDialog = (activationRun: SyncHistoryItem) => {
    analyticsTracker.eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_VIEW_DETAILS_CLICK,
      properties: {
        activationSeqId,
        activationRunId: activationRun.id
      }
    });

    setShowOpenDetailsDialog(true);
    setCurrentSyncItem(activationRun);
  };

  const getRecordFailures = () => {
    try {
      dispatch({ type: RecordsStatus.GET_ACTIVATION_RECORDS_FAILURE });
      ActivationsAPI.getActivationRunError(failedRunId).then(res => {
        if (!res.data?.errors) {
          return dispatch({
            type: RecordsStatus.SUCCESS_ACTIVATION_RECORDS_FAILURE,
            payload: {
              failureDetails: []
            }
          });
        }

        analyticsTracker.eventTrack({
          action: TRACKER_ACTIVATION_SYNC_HISTORY_SYNC_FAILURES_FETCH_SUCCESS,
          properties: {
            activationRunId: failedRunId,
            failureTypeCount: res.data.errors.length
          }
        });

        return dispatch({
          type: RecordsStatus.SUCCESS_ACTIVATION_RECORDS_FAILURE,
          payload: {
            failureDetails: res.data.errors
          }
        });
      });
    } catch (err) {
      analyticsTracker.eventTrack({
        action: TRACKER_ACTIVATION_SYNC_HISTORY_SYNC_FAILURES_FETCH_FAILED,
        properties: {
          activationRunId: failedRunId,
          errorMessage: err.message
        }
      });

      dispatch({ type: RecordsStatus.ERROR_ACTIVATION_RECORDS_FAILURE });
    }
  };

  const onStatusFilterSelect = filter => {
    let { value } = filter;

    if (filter.value === FILTER_ALL.value) {
      value = undefined;
    }

    listConductor.dispatch(new FilterList({ key: SYNC_HISTORY_STATUS_FILTER_KEY, value }));
    setStatusFilterDropdownOpen(false);
  };

  const isActiveFilterOption = filter => {
    if (filter.value === FILTER_ALL.value && !statusFilterValue) {
      return true;
    }

    return statusFilterValue === filter.value;
  };

  const getFailedRecordsDownloadDetailsObservable = () =>
    ActivationsAPI.getActivationRunErrorReport(failedRunId);

  const dataIdGenerator = getDataIdGenerator('activationHistory');

  return (
    <HdCoordinatedScrollAnchor elRef={coordinatedScrollAnchorRef.current}>
      <HdCoordinatedHeight elRef={coordinatedHeightTargetRef.current}>
        <div
          className='box box--table box--fixed-height'
          data-id={dataIdGenerator('container')}
          ref={el => {
            coordinatedHeightTargetRef.current = el;
            coordinatedScrollAnchorRef.current = el;
          }}
        >
          <div className='box__header'>
            <span className='box__title'>Sync History</span>

            <div className='box__header__actions'>
              <HdIconButton
                id='status_filter'
                className='mr-2'
                onClick={() => onStatusFilterTriggerClick()}
                dataId={dataIdGenerator('status-filter')}
              >
                <FilterIcon active={!!statusFilterValue} name='filter' size={3} />
              </HdIconButton>

              <HdMenu
                target='#status_filter'
                open={statusFilterDropdownOpen}
                onClose={() => setStatusFilterDropdownOpen(false)}
              >
                {statusFilterOptions.map((option, index) => (
                  <HdMenuItem
                    key={index}
                    selected={isActiveFilterOption(option)}
                    onClick={() => onStatusFilterSelect(option)}
                    data-id={dataIdGenerator('status-filter-options')}
                  >
                    {option.name}
                  </HdMenuItem>
                ))}
              </HdMenu>

              <HdIconButton onClick={onRefresh} edge='end' dataId={dataIdGenerator('refresh')}>
                <HdIcon
                  name='refresh'
                  size={3}
                  className={`refresh-icon ${
                    listConductorState === EntityUIState.REFRESHING ? 'refreshing' : ''
                  }`}
                />
              </HdIconButton>
            </div>
          </div>

          <HdCoordinatedScroll elRef={coordinatedScrollRef.current}>
            <div className='box__body' data-id={dataIdGenerator('')} ref={coordinatedScrollRef}>
              {canShowTable && (
                <table className={`box-table ${styles.historyTable}`}>
                  <thead>
                    <tr className='box-table-head--sticky'>
                      <th className={`${styles.startedAt} border-right`}>Started At</th>

                      <th className={styles.runTime}>Run Time</th>

                      <th className={styles.recordsQuery}>
                        Query Result <br /> Records
                        <HdTooltip title='The total number of records fetched by the Activation SQL query.'>
                          <span>
                            <HdIcon name='info' size={1} className='p-1' />
                          </span>
                        </HdTooltip>
                      </th>

                      <th className={styles.recordsIgnored}>
                        Records <br /> Ignored
                        <HdTooltip title='The number of records not picked for processing by the Activation in the run.'>
                          <span>
                            <HdIcon name='info' size={1} className='p-1' />
                          </span>
                        </HdTooltip>
                      </th>

                      <th className={styles.recordsProcessed}>
                        Records <br /> Processed
                        <HdTooltip title='The number of records picked for processing by the Activation in the run.'>
                          <span>
                            <HdIcon name='info' size={1} className='p-1' />
                          </span>
                        </HdTooltip>
                      </th>

                      <th className={styles.recordsSynced}>
                        Records
                        <br />
                        Succeeded
                        <HdTooltip title='The number of records processed successfully by the Activation at any stage in the run.'>
                          <span>
                            <HdIcon name='info' size={1} className='p-1' />
                          </span>
                        </HdTooltip>
                      </th>

                      <th className={styles.recordsFailed}>Records Failed</th>

                      <th className='actions_col border-left'>
                        <div className='center-flex-row justify-end mr-4'>View Details</div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className={styles.historyTbody}>
                    {syncHistoryItems.map((syncItem: any, index) => (
                      <React.Fragment key={index}>
                        <tr
                          className={`${
                            hasLastRunFailedRecords &&
                            shouldShowFailedEventsDetails(syncItem.id) &&
                            styles.failedRun
                          } ${index % 2 === 0 && styles.even}`}
                        >
                          <td className={styles.startedAt}>
                            <div className='flex-col align-items-start'>
                              {syncItem.getStartedTs() ? (
                                <HdCalendar value={syncItem.getStartedTs()} />
                              ) : (
                                <span>&mdash;</span>
                              )}

                              <div className='center-flex-row mt-1 text-nowrap'>
                                <div
                                  className={`badge badge-light ${syncItem.getBadgeType()}-badge`}
                                >
                                  {syncItem.status.name}

                                  {isSyncStatusFailed(syncItem) && syncItem.failureMsg && (
                                    <HdTooltip
                                      title={syncItem.failureMsg}
                                      placement='right-start'
                                      mode='surface'
                                    >
                                      <span className='hevo-icon hevo-error-filled badge-info-icon' />
                                    </HdTooltip>
                                  )}
                                </div>

                                {syncItem.isRetriedRun() && (
                                  <div>
                                    <span className='text-secondary mx-2'>&bull;</span>

                                    <span className='badge badge-light info-badge'>Retried</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className={styles.runTime}>
                            {syncItem.getRunTimeMs() ? (
                              <HdDuration
                                duration={syncItem.getRunTimeMs()}
                                options={{ useAbbr: true }}
                              />
                            ) : (
                              <span>&mdash;</span>
                            )}
                          </td>

                          <td className={styles.recordsQuery}>
                            <HdTooltip
                              title={`${syncItem.getQueryResultRecords()}`}
                              disabled={
                                !syncItem.getQueryResultRecords() ||
                                syncItem.getQueryResultRecords() < 999
                              }
                            >
                              <span>
                                {syncItem.getQueryResultRecords() ? (
                                  numFormatter(syncItem.getQueryResultRecords())
                                ) : (
                                  <span>&mdash;</span>
                                )}
                              </span>
                            </HdTooltip>
                          </td>

                          <td className={styles.recordsIgnored}>
                            {syncItem.recordsIgnored ? (
                              <HdButton
                                size='sm'
                                variation='faded'
                                color='warning'
                                icon='warning'
                                className='btn-warning'
                                onClick={() => onItemRecordsIgnoredClick(syncItem)}
                                dataId={dataIdGenerator(`record-${index}-ignored`)}
                              >
                                {numFormatter(syncItem.recordsIgnored)}

                                <HdIcon
                                  name={
                                    shouldShowIgnoredEventsDetails(syncItem.id) ? 'minus' : 'plus'
                                  }
                                  className='ml-1'
                                  size={1}
                                />
                              </HdButton>
                            ) : null}

                            {!syncItem.recordsIgnored && <span>&mdash;</span>}
                          </td>

                          <td className={styles.recordsProcessed}>
                            <HdTooltip
                              title={syncItem.recordsProcessed}
                              disabled={
                                !syncItem.recordsProcessed || syncItem.recordsProcessed < 999
                              }
                            >
                              <>
                                {syncItem.recordsProcessed ? (
                                  numFormatter(syncItem.recordsProcessed)
                                ) : (
                                  <span>&mdash;</span>
                                )}
                              </>
                            </HdTooltip>
                          </td>

                          <td className={styles.recordsSynced}>
                            <HdTooltip
                              title={getItemRecordsSuccessTooltip(syncItem)}
                              disabled={!syncItem.getRecordsSuccess()}
                            >
                              <>
                                {syncItem.getRecordsSuccess() ? (
                                  numFormatter(syncItem.getRecordsSuccess())
                                ) : (
                                  <span>&mdash;</span>
                                )}
                              </>
                            </HdTooltip>
                          </td>

                          <td className={styles.recordsFailed}>
                            {syncItem.getRecordsFailed() ? (
                              <HdButton
                                type='button'
                                size='sm'
                                variation='faded'
                                palette='error'
                                icon='replay-queue'
                                onClick={() => onItemRecordsFailedClick(syncItem)}
                                dataId={dataIdGenerator(`record-${index}-failed`)}
                              >
                                {numFormatter(syncItem.getRecordsFailed())}
                                <HdIcon
                                  name={
                                    shouldShowFailedEventsDetails(syncItem.id) ? 'minus' : 'plus'
                                  }
                                  className='ml-1'
                                  size={1}
                                />
                              </HdButton>
                            ) : null}

                            {!syncItem.getRecordsFailed() && <span>&mdash;</span>}
                          </td>

                          <td className='center-flex-row justify-end'>
                            <HdRbacButton
                              rbacPermission={RbacPermissions.ACTIVATION_EDIT}
                              type='button'
                              size='sm'
                              variation='outline'
                              icon='popup'
                              onClick={() => showDetailsDialog(syncItem)}
                              dataId={dataIdGenerator(`record-${index}-details`)}
                            >
                              Open Details
                            </HdRbacButton>
                          </td>
                        </tr>

                        {shouldShowIgnoredEventsDetails(syncItem.id) && (
                          <tr className={index % 2 === 0 && styles.even}>
                            <td className='pb-3' colSpan={8}>
                              <RecordsIgnored
                                activationRunId={ignoredRunId}
                                transformationRunStats={getIgnoredRunTransformationStats()}
                              />
                            </td>
                          </tr>
                        )}

                        {shouldShowFailedEventsDetails(syncItem.id) && (
                          <tr
                            className={clsx([
                              isEven(index) && styles.even,
                              hasLastRunFailedRecords && styles.failedRun
                            ])}
                          >
                            <td className='pb-3' colSpan={8}>
                              <RecordsFailureContext.Provider value={failedRecordsState}>
                                <RecordsFailure
                                  activationRunId={failedRunId}
                                  getRecordFailures={getRecordFailures}
                                  downloadFailureDetails={getFailedRecordsDownloadDetailsObservable}
                                />
                              </RecordsFailureContext.Provider>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}

                    {(listConductorState === EntityUIState.EMPTY ||
                      listConductorState === EntityUIState.NEW) && (
                      <tr>
                        <td colSpan={8} className='more'>
                          <div className={`load-more cursor-auto ${styles.noRecords}`}>
                            No results found in sync history.
                          </div>
                        </td>
                      </tr>
                    )}

                    <tr ref={lastElementRef} />
                  </tbody>
                </table>
              )}

              {listConductorState === EntityUIState.LOADING_MORE ? (
                <RowShimmer shimmerDimensions={shimmerDimensions} />
              ) : null}

              {listConductorState === EntityUIState.LOADING ||
              listConductorState === EntityUIState.REFRESHING ? (
                <BoxTableShimmer
                  type='history-table'
                  headerHeightDimension='78px'
                  shimmerDimensions={shimmerDimensions}
                />
              ) : null}

              {canShowTable && <ScrollToTopButton scrollViewportId={dataIdGenerator('')} />}
            </div>
          </HdCoordinatedScroll>

          {currentSyncItem && (
            <ActivationSyncHistoryItemDialog
              open={showOpenDetailsDialog}
              activationSyncHistoryItem={currentSyncItem}
              activationSeqId={activationSeqId}
              onClose={() => {
                setShowOpenDetailsDialog(false);
              }}
            />
          )}
        </div>

        {listConductorStateError && (
          <RetryApiAlert
            error={listConductorStateError}
            actionHandler={onRefresh}
            dataId={dataIdGenerator('')}
          />
        )}
      </HdCoordinatedHeight>
    </HdCoordinatedScrollAnchor>
  );
}

function RowShimmer({ shimmerDimensions }) {
  return (
    <table className='box-table history-table'>
      <tbody>
        {shimmerDimensions.map((dimension, index) => (
          <tr key={index}>
            {dimension.map((size, i) => (
              <td key={i}>
                <div className='shimmer shimmer-line mt-5' style={{ width: size }} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default ActivationSyncHistoryTable;
